import { useState } from "react";
import PulseMobileCardLayout from "@features/pulse/components/mobile/PulseMobileCardLayout";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import {
  PulseAutoPlayPaginationProps,
  PulsePageProps,
  SanitizedPulseData,
} from "@features/pulse/utils/types";
import { cn } from "@util/helpers";
import { motion, useMotionValue, useTransform } from "framer-motion";

type PulseMobileCardProps = {
  /** Individual card index number */
  index: number;
  /** Individual article to be displayed in the card */
  article: SanitizedPulseData;
} & Omit<PulsePageProps, "handleSetActiveArticle" | "offset"> &
  Omit<PulseAutoPlayPaginationProps, "isFront">;

/** Mobile Individual pulse card */
const PulseMobileCard = ({
  sortedPulseArticles,
  article,
  activeArticle,
  index,
  handleNavigateRightCard,
  autoplayTotal = 0,
  isAutoplayEnabled,
  setIsAutoplayEnabled,
  resetAutoplayLeft,
  handleSetIsAutoplayPaused,
}: PulseMobileCardProps) => {
  const { sendCustomEvent } = useSendPulseGaData();
  const [isLeft, setIsLeft] = useState<boolean>(true);
  const x = useMotionValue(0);

  const opacity = useTransform(x, [-150, 0, 150], [0, 1, 0]);
  const rotate = useTransform(x, [-150, 0, 150], [-30, 0, 30]);

  const cardIndex = sortedPulseArticles.length - index;
  const isFront = cardIndex === activeArticle;

  const isNextIndex = activeArticle + 1;
  const isNext2Index = activeArticle + 2;
  const isPreviousIndex = activeArticle - 1;

  const isFrontPrevNext = [isNextIndex, isNext2Index, isPreviousIndex].includes(
    cardIndex
  );

  const handleDragEnd = () => {
    const isSwipeLeft = x.get() < -20;
    const isSwipeRight = x.get() > 20;

    if (!isSwipeLeft && !isSwipeRight) return;

    handleNavigateRightCard();
    resetAutoplayLeft();

    if (isSwipeRight) {
      sendCustomEvent(`mobile::swipe right::${activeArticle}`, article.title);
      return;
    }

    sendCustomEvent(`mobile::swipe left::${activeArticle}`, article.title);
  };

  return (
    <motion.div
      data-testid="pulse-mobile-individual-article-card-component"
      dragDirectionLock
      drag={isFront ? "x" : false}
      className={cn(
        "relative mx-auto max-h-[700px] w-full max-w-[450px] origin-bottom -translate-x-1/2 overflow-auto rounded-lg bg-white object-cover hover:cursor-grab active:cursor-grabbing",
        "flex h-full flex-col border border-gray-175 scrollbar",
        { "shadow-pulseMobileActive": isFront },
        { hidden: !isFront && !isFrontPrevNext }
      )}
      style={{
        gridRow: 1,
        gridColumn: 1,
        x,
        opacity,
        transition: "0.1s transform, 1s opacity",
        rotate,
      }}
      animate={{
        scale: isFront ? "1" : "0.95",
        y: isFront ? 0 : 10,
        left: cardIndex < activeArticle ? (isLeft ? `-105%` : `105%`) : "auto",
      }}
      onDrag={() => {
        // Capture the drag event to determine if the card is being dragged left or right
        setIsLeft(x.get() < 0);
      }}
      dragConstraints={{
        left: 0,
        right: 0,
      }}
      onDragEnd={handleDragEnd}
    >
      <PulseMobileCardLayout
        article={article}
        autoplayTotal={autoplayTotal}
        isFront={isFront}
        isAutoplayEnabled={isAutoplayEnabled}
        setIsAutoplayEnabled={setIsAutoplayEnabled}
        resetAutoplayLeft={resetAutoplayLeft}
        handleSetIsAutoplayPaused={(isPaused) => {
          handleSetIsAutoplayPaused(isPaused);
        }}
        activeArticle={activeArticle}
      />
    </motion.div>
  );
};

export default PulseMobileCard;
